import { LoginUserBasicResponse } from "@microtica/ms-usermanagement-sdk";
import { useNavigate, useParams } from "react-router-dom";

import { securityApi, securityErrorCode, securityErrorStatus, toLoginChallenge } from "../../backend/security";
import { trackUserAdded } from "../../backend/tracking/user-settings";
import { useAuth } from "../../contexts/Auth";

export interface SetPasswordError {
    title: string;
    description: string;
}

/**
 * An invited user's first password, answering the NEW_PASSWORD_REQUIRED challenge the login page
 * stored. Shared by the classic and the new UI's views; `onError` shows what went wrong.
 */
export function useSetPassword(onError: (error: SetPasswordError) => void) {
    const { email } = useParams();
    const navigate = useNavigate();
    const { processAuthData } = useAuth();

    // The login page in the new UI shows the reason above its form
    const backToLogin = (error: SetPasswordError) => {
        onError(error);
        navigate("/auth/login", { state: { signInError: { title: error.title, hint: error.description, offerRecovery: false } } });
    };

    async function submit(password: string) {
        try {
            const storedChallenge = JSON.parse(sessionStorage.getItem("passwordChallenge") || "{}") as { username?: string; transactionId?: string };
            const transactionId = storedChallenge.transactionId;
            if (!transactionId) {
                sessionStorage.removeItem("passwordChallenge");
                backToLogin({ title: "Your sign-in session expired", description: "Please sign in again." });
                return;
            }
            const data = await securityApi.respondToLoginChallenge(transactionId, { newPassword: password });

            if (!data?.done || !data?.idToken) {
                const nextChallenge = toLoginChallenge(data, storedChallenge.username || email);
                if (!nextChallenge) {
                    throw new Error("The sign-in challenge did not return a valid transaction.");
                }
                sessionStorage.removeItem("passwordChallenge");
                sessionStorage.setItem("loginMfaChallenge", JSON.stringify(nextChallenge));
                navigate("/auth/login", { replace: true });
                return;
            }

            await processAuthData(data as LoginUserBasicResponse, "email");
            sessionStorage.removeItem("passwordChallenge");
            trackUserAdded(email!);
        } catch (err: any) {
            if (securityErrorStatus(err) === 410 || securityErrorCode(err) === "TRANSACTION_EXPIRED") {
                sessionStorage.removeItem("passwordChallenge");
                backToLogin({ title: "Your sign-in request expired", description: "For your security this only stays open for a few minutes. Please sign in again." });
                return;
            }
            onError({
                title: "Unable to set new password",
                description: "The invitation is valid for 24 hours. Contact the administrator to resend the invitation."
            });
        }
    }

    return { email, submit };
}
